// ThemeLoader — fetches brand color from /api/business-settings (public for read) and applies CSS vars
import { useEffect } from "react";
import { fetchBusinessSettings } from "@/hooks/useBusinessSettings";

// Lighten/darken hex by percent (-100..100)
const shiftHex = (hex, pct) => {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  const r = Math.max(0, Math.min(255, (num >> 16) + Math.round(255 * (pct / 100))));
  const g = Math.max(0, Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * (pct / 100))));
  const b = Math.max(0, Math.min(255, (num & 0xff) + Math.round(255 * (pct / 100))));
  return "#" + [r, g, b].map((c) => c.toString(16).padStart(2, "0")).join("");
};

// Convert #RRGGBB → "H S% L%" string (shadcn HSL channel format)
const hexToHslChannels = (hex) => {
  let h = (hex || "").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = ((g - b) / d) + (g < b ? 6 : 0);
    else if (max === g) hue = ((b - r) / d) + 2;
    else hue = ((r - g) / d) + 4;
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
};

export const applyBrandColor = (primary) => {
  if (!primary) return;
  const root = document.documentElement;
  root.style.setProperty("--brand-primary", primary);
  root.style.setProperty("--brand-primary-dark", shiftHex(primary, -10));
  root.style.setProperty("--brand-primary-light", shiftHex(primary, 15));
  // Sync shadcn --primary token (Button bg-primary etc.)
  const hsl = hexToHslChannels(primary);
  if (hsl) {
    root.style.setProperty("--primary", hsl);
    const lightness = parseInt(hsl.split(" ")[2], 10);
    root.style.setProperty(
      "--primary-foreground",
      lightness < 55 ? "0 0% 100%" : "222 47% 11%"
    );
  }
};

export const ThemeLoader = () => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchBusinessSettings();
        if (cancelled) return;
        const color = data?.theme_primary_color;
        if (color) applyBrandColor(color);
      } catch { /* silently fall back to default in :root */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return null;
};

export default ThemeLoader;
